import React, { useState, useEffect, useMemo } from 'react';
import { Star, Video, ShieldCheck, VideoOff, Share2, Ban } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import StarRating from '../../components/Testimonials/StarRating';
import RequestMessageCard from '../../components/Testimonials/RequestMessageCard';
import { getAssetUrl } from '../../utils/assetUrl';
import { ActionButton, Button, IconButton } from '../../components/UI';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'video' | 'media';
type Status = 'pending' | 'approved' | 'rejected';

interface TestimonialItem {
  _id: string;
  author: string;
  text?: string;
  country?: string;
  rating: number;
  email?: string;
  status: Status;
  order: number;
  featured?: boolean;
  video?: { url?: string; posterUrl?: string; durationSec?: number };
  consentImage?: boolean;
  consentAt?: string;
  consentMedia?: boolean;
  consentMediaAt?: string;
  createdAt: string;
}

interface EditState {
  _id: string;
  author: string;
  country: string;
  text: string;
  rating: number;
}

const statusKey: Record<Status, string> = {
  pending: 'statusPending',
  approved: 'statusApproved',
  rejected: 'statusRejected',
};

// Mesma ordem do site público (backend GET /testimonials)
const publicSort = (a: TestimonialItem, b: TestimonialItem) =>
  Number(!!b.featured) - Number(!!a.featured)
  || (a.order || 0) - (b.order || 0)
  || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

const AdminTestimonials: React.FC = () => {
  const { t, i18n } = useTranslation();
  const ta = (key: string, opts?: Record<string, unknown>) => t(`testimonialsAdmin.${key}`, opts);
  const [items, setItems] = useState<TestimonialItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [actionId, setActionId] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get('/testimonials/all');
      setItems(res.data);
    } catch (e) {
      console.error(e);
      notifications.error.network();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'approved') return items.filter((x) => x.status === 'approved').sort(publicSort);
    if (filter === 'video') return items.filter((x) => !!x.video?.url);
    if (filter === 'media') return items.filter((x) => !!x.consentMedia);
    if (filter === 'all') return items;
    return items.filter((x) => x.status === filter);
  }, [items, filter]);

  const pendingCount = items.filter((x) => x.status === 'pending').length;
  const videoCount = items.filter((x) => !!x.video?.url).length;
  const mediaCount = items.filter((x) => !!x.consentMedia).length;
  const dateLocale = i18n.language === 'en' ? 'en-GB' : i18n.language === 'es' ? 'es-ES' : 'pt-BR';

  const runAction = async (id: string, fn: () => Promise<unknown>) => {
    setActionId(id);
    try {
      await fn();
      notifications.success.settingsUpdated();
      await fetchTestimonials();
    } catch (err: any) {
      notifications.error.settingsUpdate(err.response?.data?.message || ta('errAction'));
    } finally {
      setActionId(null);
    }
  };

  const handleApprove = (id: string) => runAction(id, () => axios.patch(`/testimonials/${id}/approve`));
  const handleReject = (id: string) => runAction(id, () => axios.patch(`/testimonials/${id}/reject`));
  const handleFeature = (item: TestimonialItem) =>
    runAction(item._id, () => axios.put(`/testimonials/${item._id}`, { featured: !item.featured }));

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: ta('confirmDelete'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    runAction(id, () => axios.delete(`/testimonials/${id}`));
  };

  const handleRevokeMedia = async (id: string) => {
    if (!(await confirm({ message: ta('confirmRevokeMedia'), confirmText: ta('revokeMedia'), variant: 'danger' }))) return;
    runAction(id, () => axios.put(`/testimonials/${id}`, { revokeConsentMedia: true }));
  };

  const handleRemoveVideo = async (id: string) => {
    if (!(await confirm({ message: ta('confirmRemoveVideo'), confirmText: ta('removeVideo'), variant: 'danger' }))) return;
    runAction(id, () => axios.put(`/testimonials/${id}`, { removeVideo: true }));
  };

  // Troca de posição com o vizinho na lista de aprovados e regrava a ordem sequencial (0..n)
  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= filtered.length) return;
    const reordered = [...filtered];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const changes = reordered
      .map((item, order) => ({ item, order }))
      .filter(({ item, order }) => item.order !== order);
    runAction(filtered[index]._id, () =>
      Promise.all(changes.map(({ item, order }) => axios.put(`/testimonials/${item._id}`, { order }))));
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`/testimonials/${editing._id}`, {
        author: editing.author.trim(),
        country: editing.country.trim(),
        text: editing.text.trim(),
        rating: editing.rating,
      });
      notifications.success.settingsUpdated();
      setEditing(null);
      await fetchTestimonials();
    } catch (err: any) {
      notifications.error.settingsUpdate(err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || ta('errAction'));
    } finally {
      setSaving(false);
    }
  };

  const tabs: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: ta('tabAll') },
    { key: 'pending', label: `${ta('tabPending')}${pendingCount ? ` (${pendingCount})` : ''}` },
    { key: 'approved', label: ta('tabApproved') },
    { key: 'rejected', label: ta('tabRejected') },
    { key: 'video', label: `${ta('tabVideo')}${videoCount ? ` (${videoCount})` : ''}` },
    { key: 'media', label: `${ta('tabMedia')}${mediaCount ? ` (${mediaCount})` : ''}` },
  ];

  // Ações do depoimento: renderizadas no topo do card no desktop e no rodapé no mobile.
  const renderActions = (item: TestimonialItem, index: number, hasVideo: boolean) => {
    const busy = actionId === item._id;
    return (
    <div className="row-actions">
      {filter === 'approved' && (
        <>
          <ActionButton
            action="moveUp"
            label={ta('moveUp')}
            onClick={() => handleMove(index, -1)}
            disabled={busy || index === 0}
          />
          <ActionButton
            action="moveDown"
            label={ta('moveDown')}
            onClick={() => handleMove(index, 1)}
            disabled={busy || index === filtered.length - 1}
          />
        </>
      )}
      {item.status === 'pending' && (
        <>
          <ActionButton
            action="approve"
            label={ta('approve')}
            onClick={() => handleApprove(item._id)}
            disabled={busy}
          />
          <ActionButton
            action="reject"
            label={ta('reject')}
            onClick={() => handleReject(item._id)}
            disabled={busy}
          />
        </>
      )}
      <ActionButton
        action={item.featured ? 'unfeature' : 'feature'}
        label={item.featured ? ta('unfeature') : ta('feature')}
        onClick={() => handleFeature(item)}
        disabled={busy}
      />
      <ActionButton
        action="edit"
        label={ta('edit')}
        onClick={() => setEditing({ _id: item._id, author: item.author, country: item.country || '', text: item.text || '', rating: item.rating || 5 })}
        disabled={busy}
      />
      {hasVideo && (
        <ActionButton
          icon={VideoOff}
          label={ta('removeVideo')}
          variant="danger"
          onClick={() => handleRemoveVideo(item._id)}
          disabled={busy}
        />
      )}
      <ActionButton
        action="delete"
        label={ta('delete')}
        onClick={() => handleDelete(item._id)}
        disabled={busy}
      />
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <Breadcrumbs />
        <div className="mt-4">
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.testimonials')}</h1>
          <p className="text-gray-600">{ta('description')}</p>
        </div>

        <RequestMessageCard />

        <div className="mt-4 flex flex-wrap gap-2 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                filter === tab.key
                  ? 'bg-white border border-b-0 border-gray-200 text-primary-600 -mb-px'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 card text-center py-12 text-gray-500">
            {filter === 'all' ? ta('emptyAll') : ta('emptyFiltered')}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {filtered.map((item, index) => {
              const busy = actionId === item._id;
              const hasVideo = !!item.video?.url;
              return (
                <div
                  key={item._id}
                  className={`card border-l-4 ${
                    item.status === 'approved'
                      ? 'border-l-green-500'
                      : item.status === 'rejected'
                      ? 'border-l-red-400'
                      : 'border-l-amber-500'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    {hasVideo && (
                      <div className="sm:w-40 flex-shrink-0">
                        <video
                          src={getAssetUrl(item.video!.url!)}
                          poster={item.video?.posterUrl ? getAssetUrl(item.video.posterUrl) : undefined}
                          controls
                          playsInline
                          preload="none"
                          className="w-full aspect-[4/5] rounded-lg bg-black object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{item.author}</span>
                            {item.country && <span className="text-sm text-gray-500">— {item.country}</span>}
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              item.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : item.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {ta(statusKey[item.status])}
                            </span>
                            {item.featured && (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
                                <Star className="w-3 h-3 fill-current" />
                                {ta('featured')}
                              </span>
                            )}
                            {hasVideo && (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                                <Video className="w-3 h-3" />
                                {ta('videoBadge', { duration: item.video?.durationSec || 0 })}
                              </span>
                            )}
                          </div>
                          <div className="mt-1">
                            <StarRating value={item.rating || 5} size="sm" />
                          </div>
                        </div>
                        <div className="hidden md:block flex-shrink-0">{renderActions(item, index, hasVideo)}</div>
                      </div>
                      {item.text && <p className="text-gray-600 mt-2 italic">&quot;{item.text}&quot;</p>}
                      {hasVideo && item.consentImage && (
                        <p className="inline-flex items-center gap-1 text-xs text-green-700 mt-2">
                          <ShieldCheck className="w-3.5 h-3.5" />
                          {ta('consentGiven', { date: item.consentAt ? new Date(item.consentAt).toLocaleString(dateLocale) : '—' })}
                        </p>
                      )}
                      <div className="mt-2">
                        {item.consentMedia ? (
                          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium"
                              title={item.consentMediaAt ? ta('mediaGivenAt', { date: new Date(item.consentMediaAt).toLocaleString(dateLocale) }) : undefined}
                            >
                              <Share2 className="w-3.5 h-3.5" />
                              {ta('mediaAllowed')}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRevokeMedia(item._id)}
                              disabled={busy}
                              className="text-gray-500 underline hover:text-red-600 disabled:opacity-50"
                            >
                              {ta('revokeMedia')}
                            </button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                            <Ban className="w-3.5 h-3.5" />
                            {ta('mediaNotAllowed')}
                          </span>
                        )}
                      </div>
                      {item.email && <p className="text-xs text-gray-400 mt-1">{ta('email', { email: item.email })}</p>}
                      <p className="text-xs text-gray-400 mt-1">{new Date(item.createdAt).toLocaleString(dateLocale)}</p>
                      <div className="md:hidden mt-3">{renderActions(item, index, hasVideo)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="edit-testimonial-title">
          <form onSubmit={handleSaveEdit} className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h2 id="edit-testimonial-title" className="text-lg font-semibold text-gray-900">{ta('editTitle')}</h2>
              <IconButton action="close" label={ta('cancel')} onClick={() => setEditing(null)} />
            </div>
            <div>
              <label htmlFor="edit-author" className="block text-sm font-medium text-gray-700 mb-1">{ta('author')}</label>
              <input id="edit-author" className="input-field" value={editing.author} required maxLength={120}
                onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
            </div>
            <div>
              <label htmlFor="edit-country" className="block text-sm font-medium text-gray-700 mb-1">{ta('country')}</label>
              <input id="edit-country" className="input-field" value={editing.country} maxLength={120}
                onChange={(e) => setEditing({ ...editing, country: e.target.value })} />
            </div>
            <div>
              <label htmlFor="edit-text" className="block text-sm font-medium text-gray-700 mb-1">{ta('text')}</label>
              <textarea id="edit-text" className="input-field min-h-[120px]" value={editing.text} maxLength={2000} rows={5}
                onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">{ta('rating')}</span>
              <StarRating value={editing.rating} onChange={(rating) => setEditing({ ...editing, rating })} label={ta('rating')} />
            </div>
            <div className="form-actions pt-2">
              <Button variant="outline" type="button" onClick={() => setEditing(null)}>{ta('cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? t('common.saving') : ta('save')}</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
